import React, { useState, useEffect } from 'react';
import { Modal, Input, Table, Button, message, Select, InputNumber, Row, Col  } from 'antd';
import { SearchOutlined, SaveOutlined } from '@ant-design/icons';
import axios from '../../config/axiosInstance';
import { useAuth } from '../../context/AuthContext';

const AddEvent = ({ visible, onClose, fetchSavedEvents }) => {
  const { user } = useAuth();
  const [regasusEvents, setRegasusEvents] = useState([]);
  const [filteredRegasusEvents, setFilteredRegasusEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [host, setHost] = useState('');
  const [hosts, setHosts] = useState([]);
  const [monthsBefore, setMonthsBefore] = useState(0);
  const [monthsAfter, setMonthsAfter] = useState(2);

  // normalise the date to avoid problems with times
  const normalizeDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  useEffect(() => {
    if (visible) {
      setHosts([]);
      setHost('');
    }
  }, [visible]);

  useEffect(() => {
    const fetchHosts = async () => {
      if (!user || !user.clientID || !visible) return;
      try {
        const response = await axios.get(`/clients/regasus/${user.clientID?._id}`);
        if (response.status === 200) {
          setHosts(response.data.hosts || []);
          setHost(response.data.hosts[0] || '');
        } else {
          message.error(`Failed to fetch clients: ${response.data.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('❌ Error fetching clients:', error);
        message.error('An error occurred while fetching clients.');
      }
    };

    fetchHosts();
  }, [user, visible]);

  const fetchRegasusEvents = async () => {
    if (!host) {
      message.error('Please select a host');
      return;
    }

    setLoading(true);
    try {
      const tokenResponse = await axios.get('/regasus/token', { params: { host } });
      const token = tokenResponse.data.access_token;

      const eventsResponse = await axios.get('/regasus/events', { params: { token, host } });
      const today = normalizeDate(new Date());
      const threeMonthsAgo = normalizeDate(new Date());
      threeMonthsAgo.setMonth(today.getMonth() - monthsBefore);
      const sixMonthsAhead = normalizeDate(new Date());
      sixMonthsAhead.setMonth(today.getMonth() + monthsAfter);

      const filteredEvents = eventsResponse.data.events.event.filter((event) => {
        const eventDate = new Date(event.beginDate);
        return eventDate >= threeMonthsAgo && eventDate <= sixMonthsAhead;
      });

      const savedEventsResponse = await axios.get('/events');
      const savedEvents = savedEventsResponse.data;

      const eventsNotSaved = filteredEvents.filter(
        (event) => !savedEvents.some((savedEvent) => savedEvent.eventId === event.id.toString())
      );

      setRegasusEvents(eventsNotSaved);
      setFilteredRegasusEvents(eventsNotSaved);
    } catch (error) {
      message.error('Error fetching Regasus events');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (event) => {
    setSaving(true);
    try {
      await axios.post('/events', {
        eventId: event.id.toString(),
        name: event.name[0].value,
        label: event.label[0].value,
        mnemonic: event.mnemonic,
        location: event.location,
        city: event.city,
        countryPK: event.countryPK,
        host_url: host,
        beginDate: event.beginDate,
        endDate: event.endDate,
      });
      message.success('Event saved successfully');
      fetchSavedEvents();
      setRegasusEvents(regasusEvents.filter((e) => e.id !== event.id));
      setFilteredRegasusEvents(filteredRegasusEvents.filter((e) => e.id !== event.id));
    } catch (error) {
      console.error('Error saving event:', error.message);
      message.error('Error saving event');
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    const filtered = regasusEvents.filter(
      (event) =>
        event.mnemonic.toLowerCase().includes(value.toLowerCase()) ||
        event.id.toString().includes(value) ||
        event.beginDate.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredRegasusEvents(filtered);
  };

  const regasusColumns = [
    {
      title: 'Label',
      key: 'label',
      sorter: (a, b) => {
        const getLabel = (obj) => obj?.label?.find(l => l.lang === 'en')?.value || obj?.label?.[0]?.value || '';
        return getLabel(a).localeCompare(getLabel(b));
      },
      render: (_, record) => {
        const labelObj = record.label?.find(l => l.lang === 'en') || record.label?.[0];
        return labelObj?.value || '-';
      },
    },
    {
      title: 'Mnemonic',
      dataIndex: 'mnemonic',
      key: 'mnemonic',
    },
    {
      title: 'Begin Date',
      dataIndex: 'beginDate',
      key: 'beginDate',
      sorter: (a, b) => new Date(a.beginDate) - new Date(b.beginDate),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={() => handleSave(record)}
          loading={saving}
        >
          Save
        </Button>
      ),
    },
  ];

  const onlyAllowNumbers = (e) => {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
      <Modal
          open={visible}
          title={<div style={{ fontSize: '24px', fontWeight: 600 }}>Add Regasus Event</div>}
          width={1200}
          onCancel={onClose}
          footer={null}
      >
      <Row gutter={[16, 16]} style={{ marginTop: 16, marginBottom: 16 }}>
        {/* Host */}
        <Col xs={24} md={12}>
          <strong>Select host and search for events:</strong>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col flex="auto">
              <Select
                  style={{ width: '100%' }}
                  placeholder="Select host"
                  value={host}
                  onChange={setHost}
                  options={hosts.map((h) => ({ value: h, label: h }))}
              />
            </Col>
            <Col>
              <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={fetchRegasusEvents}
              >
                Search
              </Button>
            </Col>
          </Row>
        </Col>

        {/* Months inputs*/}
        <Col xs={24} md={12}>
          <strong>Search events from today with range of months:</strong>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col flex="auto">
              <InputNumber
                  min={0}
                  max={48}
                  value={monthsBefore}
                  onChange={setMonthsBefore}
                  placeholder="Months Before"
                  addonBefore="From"
                  style={{ width: '100%' }}
                  onKeyDown={onlyAllowNumbers}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    if (!/^\d+$/.test(pasted)) {
                      e.preventDefault();
                    }
                  }}
              />
            </Col>
            <Col flex="auto">
              <InputNumber
                  min={0}
                  max={24}
                  value={monthsAfter}
                  onChange={setMonthsAfter}
                  placeholder="Months After"
                  addonBefore="Up to"
                  style={{ width: '100%' }}
                  onKeyDown={onlyAllowNumbers}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    if (!/^\d+$/.test(pasted)) {
                      e.preventDefault();
                    }
                  }}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Input
        placeholder="Search by ID, mnemonic, or begin date"
        prefix={<SearchOutlined />}
        value={searchTerm}
        onChange={handleSearch}
        style={{ marginBottom: '16px', width: '300px' }}
      />

      <Table
        columns={regasusColumns}
        dataSource={filteredRegasusEvents}
        rowKey="id"
        loading={loading}
      />
    </Modal>
  );
};

export default AddEvent;